/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenAILiveClient } from "../lib/genai-live-client";
import { LiveClientOptions } from "../types";
import { AudioStreamer, AudioStreamerOptions } from "../lib/audio-streamer";
import { audioContext } from "../lib/utils";
import VolMeterWorket from "../lib/worklets/vol-meter";
import { sendTranscript, closeTranscriptSocket } from "../lib/transcript-socket";
import { LiveConnectConfig, Modality } from "@google/genai";

const N8N_CALL_ENDED_WEBHOOK =
  "https://hai1975.app.n8n.cloud/webhook/c741b0f5-b8e0-43c4-8193-d72bab9d02f6";

/** Danh sách giọng prebuilt (Gemini Live API). Mỗi lần Play chọn random một giọng. */
const PREBUILT_VOICES = [
  "Puck", "Charon", "Kore", "Fenrir", "Aoede",
  "Zephyr", "Orus", "Enceladus", "Leda", "Iapetus",
  "Autonoe", "Umbriel", "Erinome", "Laomedeia", "Schedar",
  "Achird", "Sadachbia", "Algieba", "Algenib", "Achernar",
  "Gacrux", "Zubenelgenubi", "Sadaltager", "Callirrhoe", "Despina",
  "Rasalgethi", "Alnilam", "Pulcherrima", "Vindemiatrix", "Sulafat",
];

function triggerProcessCompletedCall(conversationId: string): void {
  fetch(N8N_CALL_ENDED_WEBHOOK, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversationId }),
  }).catch(() => {});
}

export type UseLiveAPIResults = {
  client: GenAILiveClient;
  setConfig: (config: LiveConnectConfig) => void;
  config: LiveConnectConfig;
  model: string;
  setModel: (model: string) => void;
  connected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  /** Gọi khi user nói (gửi audio) hoặc gửi tin nhắn — để không bị auto-end sau 30s. */
  markUserActivity: () => void;
  volume: number;
  remainingSeconds: number;
  waitingForResponse: boolean;
};

const SESSION_LIMIT_SECONDS = 5 * 60; // 5 phút (chỉ hiển thị, không tự ngắt)
/** Chỉ ngắt kết nối khi bot chào tạm biệt xong; không dùng auto-end theo thời gian hay inactivity. */
/** Sau khi bot nói xong câu chào tạm biệt, đợi 2s rồi tự ngắt kết nối. */
const GOODBYE_DISCONNECT_DELAY_MS = 2000;

/** Kiểm tra nội dung lượt bot có phải là lời chào tạm biệt (kết thúc cuộc gọi) không. */
function isGoodbyeTurn(botText: string): boolean {
  const normalized = botText.trim().toLowerCase();
  return (
    normalized.includes("chào tạm biệt") ||
    normalized.includes("cảm ơn quý khách")
  );
}

/** Parse [USER_SENTIMENT: Y] và [SENTIMENT sofar : X] từ bot transcript; trả về text đã bỏ tag + sentiment (bot) + userSentiment (đánh giá user). */
function parseBotSentiment(raw: string): { text: string; sentiment?: number; userSentiment?: number } {
  let trimmed = raw.trim();
  let sentiment: number | undefined;
  let userSentiment: number | undefined;
  const userMatch = trimmed.match(/\[\s*USER_SENTIMENT\s*:\s*(\d{1,3})\s*\]/i);
  if (userMatch) {
    userSentiment = Math.min(100, Math.max(1, parseInt(userMatch[1], 10)));
    trimmed = trimmed.replace(/\[\s*USER_SENTIMENT\s*:\s*\d{1,3}\s*\]/gi, "").trim();
  }
  const match = trimmed.match(/\[\s*SENTIMENT\s+sofar\s*:\s*(\d{1,3})\s*\]/i);
  if (match) {
    sentiment = Math.min(100, Math.max(1, parseInt(match[1], 10)));
    trimmed = trimmed.replace(/\[\s*SENTIMENT\s+sofar\s*:\s*\d{1,3}\s*\]/gi, "").trim();
  }
  return { text: trimmed || raw.trim(), sentiment, userSentiment };
}

export function useLiveAPI(options: LiveClientOptions): UseLiveAPIResults {
  const client = useMemo(() => new GenAILiveClient(options), [options]);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inactivityIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Thời điểm user hoạt động lần cuối (nói hoặc gửi tin); dùng cho auto-end sau 30s không hoạt động. */
  const lastUserActivityRef = useRef<number>(0);
  /** UUID của cuộc gọi hiện tại; tạo mới mỗi lần connect, clear khi disconnect — dùng cho transcript WebSocket. */
  const conversationIdRef = useRef<string | null>(null);
  /** Gộp transcript theo lượt: gửi cả đoạn User khi Bot bắt đầu trả lời; gửi cả đoạn Bot khi User bắt đầu nói. */
  const userTranscriptBufferRef = useRef("");
  const botTranscriptBufferRef = useRef("");
  /** User message đang chờ gửi (khi bot bắt đầu), để gửi kèm userSentiment parse từ bot. */
  const pendingUserRef = useRef<{ text: string; id: string } | null>(null);
  /** Timeout gọi disconnect sau khi bot nói xong câu tạm biệt; clear khi cleanup hoặc disconnect. */
  const goodbyeDisconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Ref giữ hàm disconnect mới nhất để gọi từ onTurnComplete (tránh dùng trước khi khai báo). */
  const disconnectRef = useRef<(() => Promise<void>) | null>(null);

  const [model, setModel] = useState<string>("models/gemini-2.5-flash-native-audio-preview-12-2025");
  const [config, setConfig] = useState<LiveConnectConfig>({
    responseModalities: [Modality.AUDIO],
    speechConfig: {
      voiceConfig: {
        prebuiltVoiceConfig: {
          voiceName: "Puck", // Giọng nữ tiếng Việt chất lượng cao
        },
      },
      languageCode: "vi-VN", // Đảm bảo chỉ định mã ngôn ngữ vi-VN
    },
    systemInstruction: {
      parts: [
        {
          text: "Bạn là trợ lý AI hữu ích. PHẢN HỒI BẰNG TIẾNG VIỆT. BẠN PHẢI PHẢN HỒI MỘT CÁCH RÕ RÀNG BẰNG TIẾNG VIỆT. Hãy lắng nghe người nói cẩn thận. Nếu bạn phát hiện ngôn ngữ không phải tiếng Việt, hãy phản hồi bằng tiếng Việt trừ khi người dùng yêu cầu cụ thể ngôn ngữ khác.\n\nQuy tắc bắt buộc:\n1. Đầu mỗi lượt trả lời, ghi đúng một dòng [USER_SENTIMENT: Y] với Y từ 1 đến 100, đánh giá mức độ tích cực/tiêu cực của LỜI NGƯỜI DÙNG vừa nói (1=rất tiêu cực, 50=trung lập, 100=rất tích cực).\n2. Cuối mỗi lượt trả lời, ghi đúng một dòng [SENTIMENT sofar : X] với X từ 1 đến 100, phản ánh tình cảm cuộc hội thoại từ đầu đến nay. Ví dụ: [USER_SENTIMENT: 65] Dạ em xin... [SENTIMENT sofar : 70]",
        },
      ],
    },
    // Bật transcription để có inputTranscription (user speech) và outputTranscription (bot speech)
    inputAudioTranscription: {},
    outputAudioTranscription: {},
  });
  const [connected, setConnected] = useState(false);
  const [volume, setVolume] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [waitingForResponse, setWaitingForResponse] = useState(false);

  // register audio for streaming server -> speakers
  useEffect(() => {
    if (!audioStreamerRef.current) {
      const ua = navigator.userAgent;
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
        (typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 0 && window.innerWidth < 1024);
      const isSamsungBrowser = /SamsungBrowser|Samsung/i.test(ua);
      const streamerOptions: AudioStreamerOptions = isSamsungBrowser
        ? { initialBufferTime: 0.9, scheduleAheadTime: 1.2, checkIntervalMs: 40 }
        : isMobile
          ? { initialBufferTime: 0.6, scheduleAheadTime: 0.9, checkIntervalMs: 50 }
          : {};
      audioContext({ id: "audio-out" }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx, streamerOptions);
        audioStreamerRef.current
          .addWorklet<any>("vumeter-out", VolMeterWorket, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .then(() => {
            // Successfully added worklet
          });
      });
    }
  }, [audioStreamerRef]);

  useEffect(() => {
    const onOpen = () => {
      setConnected(true);
    };

    const onClose = () => {
      if (inactivityIntervalRef.current) {
        clearInterval(inactivityIntervalRef.current);
        inactivityIntervalRef.current = null;
      }
      setConnected(false);
      const id = conversationIdRef.current;
      if (id) {
        const ub = userTranscriptBufferRef.current.trim();
        if (ub) sendTranscript("user", ub, id);
        const bb = botTranscriptBufferRef.current.trim();
        if (bb) {
          const { text: botText, sentiment, userSentiment } = parseBotSentiment(bb);
          const pending = pendingUserRef.current;
          if (pending?.text) {
            sendTranscript("user", pending.text, pending.id, userSentiment);
            pendingUserRef.current = null;
          }
          if (botText) sendTranscript("bot", botText, id, sentiment);
        }
      }
      if (id) {
        sendTranscript("bot", "[CALL_ENDED]", id);
        triggerProcessCompletedCall(id);
      }
      conversationIdRef.current = null;
      userTranscriptBufferRef.current = "";
      botTranscriptBufferRef.current = "";
      pendingUserRef.current = null;
    };

    const onError = (error: ErrorEvent) => {
      console.error("error", error);
    };

    const stopAudioStreamer = () => {
      audioStreamerRef.current?.stop();
      setWaitingForResponse(false);
    };

    const onPleaseWait = () => setWaitingForResponse(true);

    const onAudio = (data: ArrayBuffer) => {
      setWaitingForResponse(false);
      audioStreamerRef.current?.addPCM16(new Uint8Array(data));
    };

    const onTurnComplete = () => {
      setWaitingForResponse(false);
      const bb = botTranscriptBufferRef.current.trim();
      if (bb && isGoodbyeTurn(parseBotSentiment(bb).text)) {
        if (goodbyeDisconnectTimeoutRef.current) clearTimeout(goodbyeDisconnectTimeoutRef.current);
        goodbyeDisconnectTimeoutRef.current = setTimeout(() => {
          goodbyeDisconnectTimeoutRef.current = null;
          disconnectRef.current?.();
        }, GOODBYE_DISCONNECT_DELAY_MS);
      }
    };

    const onUserTranscript = (data: { text: string }) => {
      lastUserActivityRef.current = Date.now();
      const id = conversationIdRef.current;
      if (!id) return;
      const t = data.text?.trim();
      if (!t) return;
      const ub = userTranscriptBufferRef;
      const bb = botTranscriptBufferRef;
      if (bb.current.trim()) {
        const { text: botText, sentiment, userSentiment } = parseBotSentiment(bb.current.trim());
        const pending = pendingUserRef.current;
        if (pending?.text) {
          sendTranscript("user", pending.text, pending.id, userSentiment);
          pendingUserRef.current = null;
        }
        if (botText) sendTranscript("bot", botText, id, sentiment);
        bb.current = "";
      }
      ub.current = ub.current ? `${ub.current} ${t}` : t;
    };
    const onBotTranscript = (data: { text: string }) => {
      const id = conversationIdRef.current;
      if (!id) return;
      const t = data.text?.trim();
      if (!t) return;
      const ub = userTranscriptBufferRef;
      const bb = botTranscriptBufferRef;
      if (ub.current.trim()) {
        pendingUserRef.current = { text: ub.current.trim(), id };
        ub.current = "";
      }
      bb.current = bb.current ? `${bb.current} ${t}` : t;
    };

    client
      .on("error", onError)
      .on("open", onOpen)
      .on("close", onClose)
      .on("interrupted", stopAudioStreamer)
      .on("pleaseWait", onPleaseWait)
      .on("audio", onAudio)
      .on("turncomplete", onTurnComplete)
      .on("userTranscript", onUserTranscript)
      .on("botTranscript", onBotTranscript);

    return () => {
      if (goodbyeDisconnectTimeoutRef.current) {
        clearTimeout(goodbyeDisconnectTimeoutRef.current);
        goodbyeDisconnectTimeoutRef.current = null;
      }
      client
        .off("error", onError)
        .off("open", onOpen)
        .off("close", onClose)
        .off("interrupted", stopAudioStreamer)
        .off("pleaseWait", onPleaseWait)
        .off("audio", onAudio)
        .off("turncomplete", onTurnComplete)
        .off("userTranscript", onUserTranscript)
        .off("botTranscript", onBotTranscript)
        .disconnect();
      closeTranscriptSocket();
    };
  }, [client]);

  const disconnect = useCallback(async () => {
    if (goodbyeDisconnectTimeoutRef.current) {
      clearTimeout(goodbyeDisconnectTimeoutRef.current);
      goodbyeDisconnectTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (inactivityIntervalRef.current) {
      clearInterval(inactivityIntervalRef.current);
      inactivityIntervalRef.current = null;
    }
    setRemainingSeconds(0);
    setWaitingForResponse(false);
    const id = conversationIdRef.current;
    if (id) {
      const ub = userTranscriptBufferRef.current.trim();
      if (ub) sendTranscript("user", ub, id);
      const bb = botTranscriptBufferRef.current.trim();
      if (bb) {
        const { text: botText, sentiment, userSentiment } = parseBotSentiment(bb);
        const pending = pendingUserRef.current;
        if (pending?.text) {
          sendTranscript("user", pending.text, pending.id, userSentiment);
          pendingUserRef.current = null;
        }
        if (botText) sendTranscript("bot", botText, id, sentiment);
      }
      sendTranscript("bot", "[CALL_ENDED]", id);
      triggerProcessCompletedCall(id);
    }
    conversationIdRef.current = null;
    userTranscriptBufferRef.current = "";
    botTranscriptBufferRef.current = "";
    pendingUserRef.current = null;
    audioStreamerRef.current?.stop();
    client.disconnect();
    setConnected(false);
  }, [client]);

  useEffect(() => {
    disconnectRef.current = disconnect;
  }, [disconnect]);

  const connect = useCallback(async () => {
    if (!config) {
      throw new Error("config has not been set");
    }
    const randomVoice = PREBUILT_VOICES[Math.floor(Math.random() * PREBUILT_VOICES.length)];
    const configWithRandomVoice: LiveConnectConfig = {
      ...config,
      speechConfig: {
        ...config.speechConfig,
        voiceConfig: {
          ...config.speechConfig?.voiceConfig,
          prebuiltVoiceConfig: { voiceName: randomVoice },
        },
      },
    };
    const ctx = await audioContext({ id: "audio-out" });
    if (ctx.state === "suspended") await ctx.resume();
    client.disconnect();
    const id = crypto.randomUUID();
    conversationIdRef.current = id;
    userTranscriptBufferRef.current = "";
    botTranscriptBufferRef.current = "";
    lastUserActivityRef.current = Date.now();
    sendTranscript("user", "Bắt đầu", id);
    await client.connect(model, configWithRandomVoice);
    client.send([{ text: "Bắt đầu" }]);
    setRemainingSeconds(SESSION_LIMIT_SECONDS);
    countdownIntervalRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    // Không dùng inactivity auto-disconnect: chỉ ngắt khi bot chào tạm biệt hoặc user bấm Pause.
  }, [client, config, model, disconnect]);

  const markUserActivity = useCallback(() => {
    lastUserActivityRef.current = Date.now();
  }, []);

  return {
    client,
    config,
    setConfig,
    model,
    setModel,
    connected,
    connect,
    disconnect,
    markUserActivity,
    volume,
    remainingSeconds,
    waitingForResponse,
  };
}
